import styled from "styled-components";
import { useAuthDetails } from "@/hooks/useAuthDetails";
import { PCN_REDIRECT } from "@/common/constants";

// MED-685: same visual language as the existing "Pay for your licence" banner
// (common/components/banner) — that one is Stats-page-only and licence-only;
// this one is mounted at the dashboard layout level so it persists across
// every restricted page, and covers both gates.
const BannerWrapper = styled.div`
  width: 100%;
  background-color: #e9d1aa;
  color: black;
  padding: 10px 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  text-align: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 100;

  p {
    margin: 0;
    font-size: 14px;
  }
`;

const RenewButton = styled.button`
  background-color: white;
  color: #ff9800;
  border: none;
  padding: 6px 14px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }
`;

// Resolution path per AC2: name the gate AND how it clears. Approval has no
// self-service action — it's an internal MedEx admin step — so the message
// says as much rather than pointing the user at a dead end. Licence renewal
// does have a self-service flow (the PCN portal), so it gets a CTA below.
const APPROVAL_MESSAGE =
  "Your company is awaiting MedEx admin approval. No action is needed from you — full access unlocks once your company is approved.";
const LICENCE_MESSAGE =
  "Your company needs a valid PCN licence to unlock full features.";
// When both gates fail, lead with the actionable half (the licence, which has
// a CTA) so the message doesn't open with "no action is needed" and then
// present a "Renew your licence" button.
const BOTH_MESSAGE =
  "Your company needs a valid PCN licence to unlock full features, and is also awaiting MedEx admin approval.";

export function AccessRestrictionBanner() {
  const { isCompanyApproved, hasValidLicence, isRestricted, complianceKnown } =
    useAuthDetails();

  // MED-725: the lock holds before compliance is known, but the reason is not
  // established yet, so there is nothing truthful to display.
  if (!complianceKnown || !isRestricted) return null;

  const message =
    !isCompanyApproved && !hasValidLicence
      ? BOTH_MESSAGE
      : !isCompanyApproved
        ? APPROVAL_MESSAGE
        : LICENCE_MESSAGE;

  // PCN's own login page, not MedEx's self-service — the earlier
  // `self-service/auth/register` link (no token) was landing the user back on
  // MedEx's self-service page instead of PCN's.
  function handleRenewLicence() {
    window.open(`${PCN_REDIRECT}/self-service/auth/login`, "_blank")?.focus();
  }

  return (
    <BannerWrapper>
      <p>{message}</p>
      {!hasValidLicence && (
        <RenewButton onClick={handleRenewLicence}>
          Renew your licence
        </RenewButton>
      )}
    </BannerWrapper>
  );
}
